import type {
  DimensionModifierValue,
  ImageModifierValue,
  UniversalModifierValue,
} from "schemas/generated/fileModifiers";

import React from "react";

import { deriveDimensionFromClassName } from "replo-runtime/shared/utils/dimensions";
import { extractAspectRatioFromClassName } from "replo-utils/lib/aspectRatio";

import AppearanceModifier from "./base/AppearanceModifier";
import AspectRatioModifier from "./base/AspectRatioModifier";
import { AssetModifier } from "./base/AssetModifier";
import BorderModifier from "./base/BorderModifier";
import ImageDimensionsModifier from "./base/ImageDimensionsModifier";
import MarginModifierComponent from "./base/MarginModifier";
import ObjectFitModifier from "./base/ObjectFitModifier";
import ShadowModifierComponent from "./base/ShadowModifier";
import { useOptimisticModifierValue } from "./hooks/useOptimisticModifierValue";
import ModifierLayout from "./ModifierLayout";
import {
  useComputedElementDimensions,
  useDefaultSelectedElementDesignation,
} from "./store/ModifiersStoreContext";

type AspectRatioModifierValue = Extract<
  ImageModifierValue,
  { type: "aspectRatio" }
>;

function parseAspectRatio(aspectRatio: string | null): number | null {
  if (!aspectRatio || aspectRatio === "auto") {
    return null;
  }
  const parts = aspectRatio.split("/");
  if (parts.length === 2) {
    const width = Number.parseFloat(parts[0] ?? "");
    const height = Number.parseFloat(parts[1] ?? "");
    if (!Number.isNaN(width) && !Number.isNaN(height) && height !== 0) {
      return width / height;
    }
  }
  // NOTE (Derek, 2026-01-14, REPL-20840): Handle single number ratios like "1"
  const singleValue = Number.parseFloat(aspectRatio);
  if (!Number.isNaN(singleValue)) {
    return singleValue;
  }
  return null;
}

interface ImageModifierBlockProps {
  onModifiersValueChange: (
    modifiers: UniversalModifierValue[],
    stableElementId: string,
  ) => void;
}

/**
 * Calculates the complementary dimension when aspect ratio is linked.
 * Returns null if no calculation should be done.
 */
function calculateAspectRatioDimension({
  aspectRatio,
  changedValue,
  changedSide,
}: {
  aspectRatio: number | null;
  changedValue: number | "auto" | "full";
  changedSide: "width" | "height";
}): DimensionModifierValue | null {
  if (
    aspectRatio === null ||
    typeof changedValue !== "number" ||
    changedValue <= 0
  ) {
    return null;
  }

  const complementarySide = changedSide === "width" ? "height" : "width";
  const complementaryValue =
    changedSide === "width"
      ? Math.round(changedValue / aspectRatio)
      : Math.round(changedValue * aspectRatio);

  return {
    type: "dimension",
    side: complementarySide,
    value: complementaryValue,
  };
}

const ImageModifierBlock: React.FC<ImageModifierBlockProps> = ({
  onModifiersValueChange,
}) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const computedElementDimensions = useComputedElementDimensions();

  const currentAspectRatio = useOptimisticModifierValue({
    modifierType: { type: "aspectRatio", value: "auto" },
    extractor: (elementDesignation) => {
      return extractAspectRatioFromClassName(
        elementDesignation.element.props?.className,
      );
    },
  });

  const currentWidth = useOptimisticModifierValue({
    modifierType: { type: "dimension", side: "width", value: 0 },
    extractor: (elementDesignation) => {
      return deriveDimensionFromClassName(
        elementDesignation.element.props?.className ?? undefined,
        "width",
      );
    },
  });

  const handleDimensionChange = React.useCallback(
    (modifiers: DimensionModifierValue[], side: "width" | "height") => {
      if (!defaultDesignation) {
        return;
      }
      const modifier = modifiers[0];
      if (!modifier) {
        return;
      }

      // NOTE (Derek, 2026-01-15, REPL-22387): When selecting Fill or Hug, switch aspect ratio to Custom
      // because Fill/Hug dimensions are incompatible with fixed aspect ratios
      const isNonFixedMode =
        modifier.value === "auto" || modifier.value === "full";
      if (isNonFixedMode) {
        const aspectRatioModifier: AspectRatioModifierValue = {
          type: "aspectRatio",
          value: "auto",
        };
        onModifiersValueChange(
          [modifier, aspectRatioModifier],
          defaultDesignation.element.stableElementId,
        );
        return;
      }

      const aspectRatio = parseAspectRatio(currentAspectRatio ?? null);
      const linkedModifier = calculateAspectRatioDimension({
        aspectRatio,
        changedValue: modifier.value,
        changedSide: side,
      });

      onModifiersValueChange(
        linkedModifier ? [modifier, linkedModifier] : modifiers,
        defaultDesignation.element.stableElementId,
      );
    },
    [defaultDesignation, currentAspectRatio, onModifiersValueChange],
  );

  const handleWidthChange = React.useCallback(
    (modifiers: DimensionModifierValue[]) => {
      return handleDimensionChange(modifiers, "width");
    },
    [handleDimensionChange],
  );

  const handleHeightChange = React.useCallback(
    (modifiers: DimensionModifierValue[]) => {
      return handleDimensionChange(modifiers, "height");
    },
    [handleDimensionChange],
  );

  const handleAspectRatioChange = React.useCallback(
    (modifier: AspectRatioModifierValue) => {
      if (!defaultDesignation) {
        return;
      }

      const newAspectRatio = parseAspectRatio(modifier.value);

      // NOTE (Derek, 2025-01-13): Custom aspect ratio - just send the modifier
      if (newAspectRatio === null) {
        onModifiersValueChange(
          [modifier],
          defaultDesignation.element.stableElementId,
        );
        return;
      }

      // NOTE (Derek, 2025-01-13): Non-custom aspect ratio - set both dimensions to fixed values
      const widthValue =
        typeof currentWidth === "number" && currentWidth > 0
          ? currentWidth
          : Math.round(computedElementDimensions?.width ?? 400);
      const heightValue = Math.round(widthValue / newAspectRatio);

      const widthModifier: DimensionModifierValue = {
        type: "dimension",
        side: "width",
        value: widthValue,
      };
      const heightModifier: DimensionModifierValue = {
        type: "dimension",
        side: "height",
        value: heightValue,
      };

      onModifiersValueChange(
        [modifier, widthModifier, heightModifier],
        defaultDesignation.element.stableElementId,
      );
    },
    [
      defaultDesignation,
      currentWidth,
      computedElementDimensions,
      onModifiersValueChange,
    ],
  );

  if (!defaultDesignation) {
    return null;
  }

  const sections = [
    {
      rows: [
        {
          title: "Media",
          component: (
            <AssetModifier
              onAssetChange={(assetSource) => {
                onModifiersValueChange(
                  [{ type: "asset", value: assetSource ?? "" }],
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
      ],
    },
    {
      rows: [
        {
          title: "Size",
          component: (
            <ImageDimensionsModifier
              onWidthChange={handleWidthChange}
              onHeightChange={handleHeightChange}
            />
          ),
        },
        {
          title: "Aspect Ratio",
          component: <AspectRatioModifier onChange={handleAspectRatioChange} />,
        },
        {
          title: "Object Fit",
          component: (
            <ObjectFitModifier
              onChange={(modifier) => {
                onModifiersValueChange(
                  [modifier],
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
      ],
    },
    {
      rows: [
        {
          title: "Margin",
          component: (
            <MarginModifierComponent
              onChange={(modifiers) => {
                onModifiersValueChange(
                  modifiers,
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
      ],
    },
    {
      rows: [
        {
          title: "Border",
          component: (
            <BorderModifier
              onChange={(modifiers) => {
                onModifiersValueChange(
                  modifiers,
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
        {
          title: "Appearance",
          component: (
            <AppearanceModifier
              onChange={(modifiers) => {
                onModifiersValueChange(
                  modifiers,
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
        {
          title: "Shadow",
          component: (
            <ShadowModifierComponent
              onChange={(modifiers) => {
                onModifiersValueChange(
                  modifiers,
                  defaultDesignation.element.stableElementId,
                );
              }}
            />
          ),
        },
      ],
    },
  ];

  return (
    <ModifierLayout
      sections={sections}
      onModifiersValueChange={onModifiersValueChange}
    />
  );
};

export default ImageModifierBlock;
